import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import { statutoryBookName } from '../../../utils/countryFeatures';
import ChartOfAccounts from '../../Bookkeeping/ChartOfAccounts';
import VendorAccounts from '../../Bookkeeping/VendorAccounts';
import ManualEntry from '../../Bookkeeping/ManualEntry';
import DraftEntries from '../../Bookkeeping/DraftEntries';
import EntityInfo from '../../Bookkeeping/EntityInfo';
import CompliancePanel from '../../Bookkeeping/CompliancePanel';
import { ClosePeriod, OpenPeriodForm } from '../../Bookkeeping/StatutoryBooks';
import HtmlViewer from '../HtmlViewer';
import type { AwCtx } from '../ctx';
import { btn, card, dmy, errBox, note, num, openBlob, pillGreen, pillGrey, sectionHead, table, td, th } from '../ui';

// ── Accounting panes (owner 2026-10-13) ─────────────────────────────────────────────────────────
// Each submenu mounts the panel StatutoryBooks already mounted, with the same props, under the
// shell's shared period. Books and ledgers stay what they are on the server — printable documents —
// so the General ledger and Trial balance show the print HTML inline and keep the PDF a click away.

type Book = { kind: string; label: string; bankAccountId: string; entries: number };

export function ChartPane({ ctx }: { ctx: AwCtx }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ChartOfAccounts clientId={ctx.clientId} defaultOpen />
      <VendorAccounts clientId={ctx.clientId} onChanged={ctx.bump} />
    </div>
  );
}

/** The five daily journals as PDF cards — copied from StatutoryBooks so the workspace's Journal
 *  entries page offers the books the entries land in, not only the entry forms. */
function JournalCards({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [books, setBooks] = useState<Book[] | null>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.ledgerAvailable(ctx.periodId).then((d: any) => { if (alive) setBooks((d.books || []).filter((b: Book) => b.kind !== 'gl')); }).catch(() => { if (alive) setBooks([]); });
    return () => { alive = false; };
  }, [ctx.periodId, ctx.reload]);
  const open = async (key: string, kind: string, bankAccountId: string) => {
    if (busy) return; setBusy(key); setErr('');
    try { openBlob(await api.ledgerBookUrl(`journal/${kind}`, ctx.periodId, bankAccountId ? { bankAccountId } : {})); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  if (books === null) return null;
  return (
    <div>
      <div style={sectionHead}>สมุดรายวัน · {t('pa_sb_h_journals')}</div>
      {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
      {!books.length ? <div style={note}>{t('pa_sb_nothing_posted')}</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 8 }}>
          {books.map(b => {
            // A bank book is one volume per account NUMBER — the Act's shape, so one card per account.
            const key = b.kind + b.bankAccountId;
            const label = statutoryBookName(b.kind) || b.label || b.kind;
            return (
              <button key={key} style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left', width: '100%', opacity: busy === key ? 0.6 : 1 }}
                onClick={() => open(key, b.kind, b.bankAccountId)}>
                <i className={busy === key ? 'ti ti-loader' : 'ti ti-book'} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{label}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.label}{b.bankAccountId ? ` · ${b.bankAccountId}` : ''} · {t('pa_sb_n_entries', { count: b.entries })}
                  </span>
                </span>
                <i className="ti ti-file-type-pdf" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function JournalPane({ ctx }: { ctx: AwCtx }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ManualEntry clientId={ctx.clientId} periodId={ctx.periodId} periodClosed={ctx.periodClosed} onChanged={ctx.bump} />
      <DraftEntries clientId={ctx.clientId} periodId={ctx.periodId} periodClosed={ctx.periodClosed} refreshKey={ctx.reload} onPosted={ctx.bump} />
      <JournalCards ctx={ctx} />
    </div>
  );
}

/** Buttons that open a ledger-books PDF in a new tab, with the spinner every download button owes. */
export function BookButtons({ ctx, items }: { ctx: AwCtx; items: { key: string; th: string; glossKey: string; path: string }[] }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const open = async (k: string, path: string) => {
    if (busy) return; setBusy(k); setErr('');
    try { openBlob(await api.ledgerBookUrl(path, ctx.periodId)); } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {items.map(i => (
          <button key={i.key} onClick={() => open(i.key, i.path)} disabled={!!busy} title={t(i.glossKey)} style={{ ...btn, opacity: busy === i.key ? 0.6 : 1 }}>
            <i className={busy === i.key ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {i.th} <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>· {t(i.glossKey)}</span>
          </button>
        ))}
      </div>
      {err && <div style={{ ...errBox, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

export function LedgerPane({ ctx, book }: { ctx: AwCtx; book: 'general-ledger' | 'trial-balance' }) {
  const isGl = book === 'general-ledger';
  const items = isGl
    ? [{ key: 'gl', th: 'บัญชีแยกประเภท', glossKey: 'pa_sb_c_gl', path: 'general-ledger' },
       { key: 'ar', th: 'บัญชีย่อยลูกหนี้', glossKey: 'pa_sb_c_ar', path: 'subsidiary/ar' },
       { key: 'ap', th: 'บัญชีย่อยเจ้าหนี้', glossKey: 'pa_sb_c_ap', path: 'subsidiary/ap' }]
    : [{ key: 'tb', th: 'งบทดลอง', glossKey: 'pa_sb_c_tb', path: 'trial-balance' }];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <BookButtons ctx={ctx} items={items} />
      <HtmlViewer base="ledger-books" path={book} qs={{ periodId: ctx.periodId }} reload={ctx.reload} />
    </div>
  );
}

export function PeriodsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={sectionHead}>{t('aw_periods_title')}</div>
        <div style={{ ...card, padding: 0 }}>
          <table style={table}>
            <thead><tr><th style={th}>{t('aw_col_start')}</th><th style={th}>{t('aw_col_end')}</th><th style={th}>{t('aw_col_status')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_col_entries')}</th></tr></thead>
            <tbody>
              {ctx.periods.map(p => (
                <tr key={p.id} style={p.id === ctx.periodId ? { background: 'var(--accent-bg)' } : undefined}>
                  <td style={td}>{dmy(p.start_date)}</td><td style={td}>{dmy(p.end_date)}</td>
                  <td style={td}>{p.status === 'closed' ? <span style={pillGreen}>ปิดบัญชีแล้ว · {t('pa_sb_status_closed')}</span> : <span style={pillGrey}>{t('aw_period_open')}</span>}</td>
                  <td style={num}>{p.entries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* One open period per client is a database constraint; the form surfaces the server's refusal. */}
      {ctx.periods.every(p => p.status === 'closed') && <OpenPeriodForm clientId={ctx.clientId} onOpened={ctx.refreshPeriods} />}
      <EntityInfo clientId={ctx.clientId} periodClosed={ctx.periodClosed} onChanged={ctx.bump} />
    </div>
  );
}

export function ClosingPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {ctx.periodClosed
        ? <div style={{ ...card, color: 'var(--text-tertiary)', fontSize: 12.5 }}><i className="ti ti-lock" aria-hidden /> {t('aw_period_closed_msg')}</div>
        : <div style={card}><ClosePeriod periodId={ctx.periodId} onClosed={ctx.refreshPeriods} /></div>}
      <CompliancePanel clientId={ctx.clientId} periodId={ctx.periodId} />
    </div>
  );
}
